import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { X, Loader2 } from 'lucide-react'
import { curriculumBuilderApi, type AdminContent, type CreateDirectContentRequest } from '@/api/admin'

const LEVEL_LABELS: Record<string, string> = {
  curriculum: '커리큘럼',
  course: '코스',
  module: '모듈',
}

interface CreateContentModalProps {
  level: 'curriculum' | 'course' | 'module'
  parentId?: number
  parentTitle?: string
  onClose: () => void
  onCreated: (content: AdminContent) => void
}

export function CreateContentModal({ level, parentId, parentTitle, onClose, onCreated }: CreateContentModalProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  const createMutation = useMutation({
    mutationFn: (data: CreateDirectContentRequest) => curriculumBuilderApi.createContent(data),
    onSuccess: (content) => onCreated(content),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return

    createMutation.mutate({
      hierarchyLevel: level,
      title: title.trim(),
      description: description.trim() || undefined,
      parentId,
    })
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            새 {LEVEL_LABELS[level]} 만들기
          </h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {parentTitle && (
            <div className="text-sm text-slate-500">
              상위: <span className="font-medium text-slate-700">{parentTitle}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">제목 *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`${LEVEL_LABELS[level]} 제목을 입력하세요`}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">설명</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="설명 (선택사항)"
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
            />
          </div>

          {createMutation.isError && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              생성에 실패했습니다. 다시 시도해주세요.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={!title.trim() || createMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              만들기
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}
